import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

import type { ProgressEntry } from '../types';

interface StepProgressProps {
  entries: ProgressEntry[];
  running: boolean;
  finalError: string | null;
}

export const StepProgress: React.FC<StepProgressProps> = ({ entries, running, finalError }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold mb-1">
          {running
            ? t('organizations.import.progress.titleRunning', 'Import en cours...')
            : finalError
              ? t('organizations.import.progress.titleError', 'Import échoué')
              : t('organizations.import.progress.titleDone', 'Import terminé')}
        </h4>
        <p className="text-sm text-muted-foreground">
          {running
            ? t(
                'organizations.import.progress.descriptionRunning',
                'Création des ressources dans l\'ordre. Ne fermez pas cette fenêtre.',
              )
            : finalError
              ? t(
                  'organizations.import.progress.descriptionError',
                  'Une erreur est survenue. Toutes les ressources créées ont été annulées.',
                )
              : t('organizations.import.progress.descriptionDone', 'Toutes les ressources ont été créées avec succès.')}
        </p>
      </div>

      <ul className="rounded-md border border-border divide-y divide-border max-h-80 overflow-y-auto">
        {entries.map((e) => (
          <li key={e.id} className="px-3 py-2 flex items-center gap-2 text-sm">
            <StatusIcon status={e.status} />
            <span className="flex-1 truncate" title={e.label}>{e.label}</span>
            {e.detail && (
              <span className="text-xs text-muted-foreground truncate max-w-[40%]" title={e.detail}>
                {e.detail}
              </span>
            )}
          </li>
        ))}
        {entries.length === 0 && (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('organizations.import.progress.empty', 'En attente...')}
          </li>
        )}
      </ul>

      {finalError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">
              {t('organizations.import.progress.rollbackTitle', 'Tout a été annulé')}
            </div>
            <div className="text-xs mt-1">{finalError}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusIcon: React.FC<{ status: ProgressEntry['status'] }> = ({ status }) => {
  switch (status) {
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
    default:
      return <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />;
  }
};

export default StepProgress;
